import React, { useEffect, useState, useRef } from "react";
import Resource from "./Resource";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import iconUrl from "../../../assets/svg/marker-single.svg";
import iconGroupUrl from "../../../assets/svg/marker-group.svg";

const singleScale = 1.15;
const markerSingle = new L.Icon({
  iconUrl: iconUrl,
  iconSize: [28 * singleScale, 36 * singleScale],
  iconAnchor: [12 * singleScale, 32 * singleScale],
  popupAnchor: [0, -32 * singleScale],
});

const groupScale = 1.25;
const markerGroup = new L.Icon({
  iconUrl: iconGroupUrl,
  iconSize: [32 * groupScale, 32 * groupScale],
  iconAnchor: [16 * groupScale, 21.5 * groupScale],
  popupAnchor: [0, -16 * groupScale],
});

export default function ResourceMap({ results, userList, handleUserListUpdate, isMobile }) {
  const [inMapResults, setInMapResults] = useState([]);
  const [markers, setMarkers] = useState([]);
  const [activeResourceIds, setActiveResourceIds] = useState([]);
  const mapRef = useRef(null);
  const markerRefs = useRef([]);
  const [printId, setPrintId] = useState(null);
  const maxIndex = results.length + 100;

  // Initialize markers and inMapResults when results change
  useEffect(() => {
    const uniqueResults = results.filter((result, index, self) => {
      return index === self.findIndex((t) => t.item.id === result.item.id);
    });
    setInMapResults(uniqueResults);

    // Group closely located resources by rounding their lat/lng
    const roundingFactor = 500;

    helloMarkers = uniqueResults.reduce((acc, resource) => {
      resource.item.contactDetails.find((contact) => {
        if (
          (contact.type =
            "PhysicalLocation" && contact.contact.latitude && contact.contact.longitude)
        ) {
          const key = `${Math.round(contact.contact.latitude * roundingFactor) / roundingFactor},${
            Math.round(contact.contact.longitude * roundingFactor) / roundingFactor
          }`;
          if (typeof acc[key] === "undefined") {
            acc[key] = [];
          }
          acc[key].push(resource);
        }
      });
      return acc;
    }, {});

    const markers = Object.keys(helloMarkers).map((key) => {
      const latLng = key.split(",");
      return latLng &&
        latLng[0] > 48 &&
        latLng[0] < 60 &&
        latLng[1] < -114 &&
        latLng[1] > -139 ? ( // BC, roughly.
        <Marker
          position={latLng}
          key={key}
          ref={(ref) => markerRefs.current.push(ref)}
          icon={helloMarkers[key].length == 1 ? markerSingle : markerGroup}
          eventHandlers={{
            click: () => {
              setActiveResourceIds(helloMarkers[key].map((i) => i.item.id));
              const markerRef = markerRefs.current.find((ref) => ref._latlng.lat == latLng[0]);

              // Reset zIndex of current active marker
              markerRefs.current.forEach((ref) => {
                if (ref && ref.getElement().style.zIndex == maxIndex) {
                  ref.getElement().style.zIndex = ref._zIndex;
                }
              });

              // Set zIndex of clicked marker
              if (markerRef) {
                markerRef.getElement().style.zIndex = maxIndex;
              }
            },
          }}
        >
          <Popup className="h5" closeButton={false}>
            {helloMarkers[key].slice(0, 5).map((resource) => (
              <div style={{ marginTop: "10px" }}>
                <a href={`#${resource.item.id}`}>{resource.item.name_primary}</a>
              </div>
            ))}
            {helloMarkers[key].length > 5 && (
              <div style={{ marginTop: "10px", fontStyle: "italic" }}>
                <a href="#resources">+ {helloMarkers[key].length - 5} more</a>
              </div>
            )}
          </Popup>
        </Marker>
      ) : null;
    });
    setMarkers(markers.filter((marker) => marker));
  }, [results]);

  // Update map center and zoom when markers change
  useEffect(() => {
    if (markers.length > 0) {
      const latLngs = markers.map((marker) => marker.props.position);
      const latitudes = latLngs.map((latLng) => latLng[0]);
      const longitudes = latLngs.map((latLng) => latLng[1]);
      const maxLat = Math.max(...latitudes);
      const minLat = Math.min(...latitudes);
      const maxLng = Math.max(...longitudes);
      const minLng = Math.min(...longitudes);

      const map = mapRef.current;
      if (map) {
        map.flyToBounds(
          [
            [minLat, minLng],
            [maxLat, maxLng],
          ],
          { padding: [25, 25] }
        );
      }
    }
  }, [markers]);

  // Update inMapResults when map moves
  useEffect(() => {
    const map = mapRef.current;
    if (map) {
      map.on("moveend", () => {
        const newBounds = map.getBounds();
        const newResults = inMapResults.filter((result) => {
          if (!result.item.lat_lng) {
            return false;
          }
          const latLng = result.item.lat_lng.split(",");
          const latLngBounds = L.latLngBounds([
            [newBounds._southWest.lat, newBounds._southWest.lng],
            [newBounds._northEast.lat, newBounds._northEast.lng],
          ]);
          return latLngBounds.contains(L.latLng(parseFloat(latLng[0]), parseFloat(latLng[1])));
        });
        setInMapResults(newResults);
      });
      map.on("click", (e) => {
        if (map.length > 0 && map.find((m) => m._popup._isOpen)) {
          return;
        } else {
          setActiveResourceIds([]);
        }
      });
    }
  }, [mapRef, markers]);

  // check if the component is visible, and invalidateSize if it is
  // useEffect(() => {
  //   const observer = new IntersectionObserver(
  //     (entries) => {
  //       if (entries[0].isIntersecting) {
  //         mapRef.current.invalidateSize();
  //       }
  //     },
  //     { threshold: 0.5 }
  //   );
  //   observer.observe(document.querySelector(".resourceMap"));
  //   return () => observer.disconnect();
  // }, []);

  const handlePrint = (resourceId) => {
    setPrintId(resourceId);
    setTimeout(() => {
      window.print();
      setPrintId(null);
    }, 1000); // Adjust the delay time as needed
  };

  return (
    <>
      <MapContainer
        center={[49.276684, -123.112404]} // Temporary center
        zoom={5}
        style={{
          height: "50vh",
          minHeight: "400px",
          width: "100%",
        }}
        scrollWheelZoom={false}
        className="resourceMap"
        ref={mapRef}
      >
        <TileLayer
          // url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          // url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
          url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
          // url="https://tiles.stadiamaps.com/tiles/osm_bright/{z}/{x}/{y}{r}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />
        {markers}
      </MapContainer>
      <div
        className={`content content--wide wrapper ${!printId ? "" : "print-only"}`}
        id="resources"
      >
        <div className="resource-list">
          <ul className="resultCards">
            {inMapResults &&
              inMapResults.map((resource, index) => (
                <li
                  key={index}
                  id={resource.item.id}
                  className={`${activeResourceIds.includes(resource.item.id) ? "active" : ""} ${
                    resource.item.id === printId ? "print-only" : ""
                  }`}
                >
                  {
                    <Resource
                      resourceObject={resource}
                      userList={userList}
                      handleUserListUpdate={handleUserListUpdate}
                      handlePrint={handlePrint}
                      isMobile={isMobile}
                    />
                  }
                </li>
              ))}
          </ul>
        </div>
      </div>
    </>
  );
}
